import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { LoginRequest, LoginResponse, ChangePinRequest } from '@trackkm/shared';
import { api } from '@/lib/api';
import { queryClient } from '@/lib/query-client';
import { useAuthStore } from '@/stores/auth';

export function useLogin() {
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const response = await api.post<LoginResponse>('/auth/login', credentials);
      if (!response.data) {
        throw new Error(response.error ?? 'Login failed');
      }
      return response.data;
    },
    onSuccess: (data) => {
      login(data.token, data.employee, data.mustChangePin ?? false);
      if (data.mustChangePin) {
        navigate('/change-pin', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    },
  });
}

export function useChangePin() {
  return useMutation({
    mutationFn: async (data: ChangePinRequest) => {
      const response = await api.post<{ message: string }>('/auth/change-pin', data);
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to change PIN');
      }
      return response.data;
    },
  });
}

export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  return () => {
    logout();
    queryClient.clear();
    navigate('/login', { replace: true });
  };
}
